import { Controller } from '@hotwired/stimulus'

declare global {
  interface Window {
    Chartkick?: {
      eachChart: (callback: (chart: any) => void) => void
      charts: { [key: string]: any }
    }
    ChartPatterns?: {
      applyPatternsToChart: (chartId: string) => boolean
    }
  }
}

// Chartkick doesn't automatically reload after Turbo Stream/Frame updates.
// This controller forces chart re-initialization by re-executing the inline scripts.
export default class extends Controller {
  private reloadTimeout: number | null = null

  connect(): void {
    this.reloadCharts()
    this.applyPatternsIfNeeded()
    this.element.addEventListener('turbo:frame-load', this.handleTurboLoad.bind(this))
    this.element.addEventListener('turbo:before-stream-render', this.handleTurboStream.bind(this))
  }

  disconnect(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
    }
    this.element.removeEventListener('turbo:frame-load', this.handleTurboLoad.bind(this))
    this.element.removeEventListener('turbo:before-stream-render', this.handleTurboStream.bind(this))
  }

  private handleTurboLoad(): void {
    this.scheduleReload()
  }

  private handleTurboStream(): void {
    this.scheduleReload()
  }

  private scheduleReload(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
    }
    this.reloadTimeout = window.setTimeout(() => {
      this.reloadCharts()
      this.applyPatternsIfNeeded()
    }, 200)
  }

  private applyPatternsIfNeeded(): void {
    const usePatterns = this.element.getAttribute('data-chart-patterns') === 'true'
    if (!usePatterns) {
      return
    }

    // Wait for Chartkick to render the chart
    requestAnimationFrame(() => {
      setTimeout(() => {
        const chartId = this.element.querySelector('[id$="--borda-kick-chart"]')?.id
        if (chartId && window.ChartPatterns) {
          window.ChartPatterns.applyPatternsToChart(chartId)
        }
      }, 100)
    })
  }

  private reloadCharts(): void {
    if (typeof window.Chartkick === 'undefined' || !window.Chartkick) {
      return
    }

    requestAnimationFrame(() => {
      const chartScripts = this.element.querySelectorAll('script')
      
      chartScripts.forEach((script: HTMLScriptElement) => {
        const scriptContent = script.textContent || script.innerHTML
        
        if (scriptContent && (scriptContent.includes('new Chartkick') || scriptContent.includes('Chartkick.'))) {
          try {
            const newScript = document.createElement('script')
            newScript.textContent = scriptContent
            script.parentNode?.replaceChild(newScript, script)
          } catch (error) {
            console.error('Failed to reload chart script:', error)
          }
        }
      })
    })
  }

  reload(): void {
    this.reloadCharts()
    this.applyPatternsIfNeeded()
  }
}
